import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  actionItems: "Action Items",
  needsAttention: "Items that need your attention",
  noPendingActions: "No pending actions. You're all caught up!",
  signatureBadge: "Signature",
  inactiveBadge: "Inactive",
  failedBadge: "Failed",
  expires: (when: string) => `Expires ${when}`,
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    actionItems: "Actions à mener",
    needsAttention: "Éléments qui requièrent votre attention",
    noPendingActions: "Aucune action en attente. Vous êtes à jour !",
    signatureBadge: "Signature",
    inactiveBadge: "Inactif",
    failedBadge: "Échec",
    expires: (when) => `Expire ${when}`,
  },
  es_ES: {
    actionItems: "Tareas pendientes",
    needsAttention: "Elementos que requieren tu atención",
    noPendingActions: "No hay acciones pendientes. ¡Lo tienes todo al día!",
    signatureBadge: "Firma",
    inactiveBadge: "Inactivo",
    failedBadge: "Fallido",
    expires: (when) => `Caduca ${when}`,
  },
  pt_BR: {
    actionItems: "Itens de ação",
    needsAttention: "Itens que precisam da sua atenção",
    noPendingActions: "Nenhuma ação pendente. Está tudo em dia!",
    signatureBadge: "Assinatura",
    inactiveBadge: "Inativo",
    failedBadge: "Falhou",
    expires: (when) => `Expira ${when}`,
  },
  de_DE: {
    actionItems: "Offene Aufgaben",
    needsAttention: "Punkte, die Ihre Aufmerksamkeit erfordern",
    noPendingActions: "Keine offenen Aufgaben. Sie sind auf dem neuesten Stand!",
    signatureBadge: "Signatur",
    inactiveBadge: "Inaktiv",
    failedBadge: "Fehlgeschlagen",
    expires: (when) => `Läuft ab ${when}`,
  },
  it_IT: {
    actionItems: "Azioni da svolgere",
    needsAttention: "Elementi che richiedono la tua attenzione",
    noPendingActions: "Nessuna azione in sospeso. Sei in pari!",
    signatureBadge: "Firma",
    inactiveBadge: "Inattivo",
    failedBadge: "Non riuscito",
    expires: (when) => `Scade ${when}`,
  },
  ar: {
    actionItems: "إجراءات مطلوبة",
    needsAttention: "عناصر تحتاج إلى اهتمامك",
    noPendingActions: "لا توجد إجراءات معلّقة. لقد أنجزت كل شيء!",
    signatureBadge: "توقيع",
    inactiveBadge: "غير نشط",
    failedBadge: "فشل",
    expires: (when) => `تنتهي ${when}`,
  },
  ja_JP: {
    actionItems: "対応事項",
    needsAttention: "対応が必要な項目",
    noPendingActions: "保留中の対応事項はありません。すべて完了しています。",
    signatureBadge: "署名",
    inactiveBadge: "非アクティブ",
    failedBadge: "失敗",
    expires: (when) => `${when}に期限切れ`,
  },
  hi_IN: {
    actionItems: "कार्य आइटम",
    needsAttention: "वे आइटम जिन पर आपका ध्यान आवश्यक है",
    noPendingActions: "कोई लंबित कार्य नहीं। सब कुछ पूरा है!",
    signatureBadge: "हस्ताक्षर",
    inactiveBadge: "निष्क्रिय",
    failedBadge: "विफल",
    expires: (when) => `${when} समाप्त होगा`,
  },
  zh_CN: {
    actionItems: "待办事项",
    needsAttention: "需要您处理的事项",
    noPendingActions: "没有待处理事项，一切已处理完毕！",
    signatureBadge: "签署",
    inactiveBadge: "非活跃",
    failedBadge: "失败",
    expires: (when) => `${when}过期`,
  },
  zh_TW: {
    actionItems: "待辦事項",
    needsAttention: "需要您處理的項目",
    noPendingActions: "沒有待處理事項，一切都已處理完畢！",
    signatureBadge: "簽署",
    inactiveBadge: "非活躍",
    failedBadge: "失敗",
    expires: (when) => `${when}到期`,
  },
  nl_NL: {
    actionItems: "Actiepunten",
    needsAttention: "Items die je aandacht nodig hebben",
    noPendingActions: "Geen openstaande acties. Je bent helemaal bij!",
    signatureBadge: "Ondertekening",
    inactiveBadge: "Inactief",
    failedBadge: "Mislukt",
    expires: (when) => `Verloopt ${when}`,
  },
  nb_NO: {
    actionItems: "Handlingspunkter",
    needsAttention: "Elementer som trenger din oppmerksomhet",
    noPendingActions: "Ingen ventende handlinger. Du er à jour!",
    signatureBadge: "Signatur",
    inactiveBadge: "Inaktiv",
    failedBadge: "Feilet",
    expires: (when) => `Utløper ${when}`,
  },
  sv_SE: {
    actionItems: "Att göra",
    needsAttention: "Poster som behöver din uppmärksamhet",
    noPendingActions: "Inga väntande åtgärder. Du är helt ikapp!",
    signatureBadge: "Signatur",
    inactiveBadge: "Inaktiv",
    failedBadge: "Misslyckad",
    expires: (when) => `Löper ut ${when}`,
  },
  fi_FI: {
    actionItems: "Tehtävät",
    needsAttention: "Kohteet, jotka vaativat huomiotasi",
    noPendingActions: "Ei odottavia tehtäviä. Kaikki on hoidettu!",
    signatureBadge: "Allekirjoitus",
    inactiveBadge: "Passiivinen",
    failedBadge: "Epäonnistui",
    expires: (when) => `Vanhenee ${when}`,
  },
  da_DK: {
    actionItems: "Opgaver",
    needsAttention: "Punkter, der kræver din opmærksomhed",
    noPendingActions: "Ingen afventende handlinger. Du er helt ajour!",
    signatureBadge: "Signatur",
    inactiveBadge: "Inaktiv",
    failedBadge: "Mislykkedes",
    expires: (when) => `Udløber ${when}`,
  },
  is_IS: {
    actionItems: "Verkefni",
    needsAttention: "Atriði sem þarfnast athygli þinnar",
    noPendingActions: "Engar aðgerðir í bið. Þú ert búin(n) með allt!",
    signatureBadge: "Undirritun",
    inactiveBadge: "Óvirkt",
    failedBadge: "Mistókst",
    expires: (when) => `Rennur út ${when}`,
  },
};
